import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PageHero from '../components/common/PageHero';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import {
  Users,
  UserCheck,
  UserX,
  UserPlus,
  Search,
  Crown,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Trash2,
  Shield,
  Bell,
  Send,
  KeyRound,
  Power,
  DollarSign,
  Save,
  Pencil,
  Copy,
  ArrowLeft
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';

const emptyAddUserForm = {
  full_name: '',
  email: '',
  preferred_designation: '',
  alternate_contact: '',
  country: 'South Africa',
  province: '',
  city: '',
  membership_type: 'doctor',
  practice_name: '',
  ahpcsa_number: '',
  bhf_number: '',
  student_number: '',
  public_email: '',
  public_phone: '',
  public_address_line1: '',
  public_address_line2: '',
  is_approved: false,
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  const [activeTab, setActiveTab] = useState('pending');
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all');
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    doctors: 0,
    students: 0
  });
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [notifForm, setNotifForm] = useState({ title: '', message: '', user_id: '' });
  const [membershipPrices, setMembershipPrices] = useState({ doctor: 1200, student: 600 });
  const [priceLoading, setPriceLoading] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [notifUserSearch, setNotifUserSearch] = useState('');
  const [notifUserResults, setNotifUserResults] = useState([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState(emptyAddUserForm);
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [addUserResult, setAddUserResult] = useState(null);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingUsers();
    } else if (activeTab === 'all') {
      fetchAllUsers();
    } else if (activeTab === 'notifications') {
      fetchAdminNotifications();
    } else if (activeTab === 'pricing') {
      fetchMembershipPrices();
    }
  }, [activeTab]);

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${backendUrl}/api/admin/users/pending`);
      setPendingUsers(response.data.users || []);
      setStats(prev => ({ ...prev, pending: response.data.count || 0 }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch pending users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (membershipFilter !== 'all') params.membership_type = membershipFilter;
      
      const response = await axios.get(`${backendUrl}/api/admin/users`, { params });
      setUsers(response.data.users || []);
      setStats(prev => ({
        ...prev,
        total: response.data.total || 0,
        approved: response.data.users?.filter(u => u.is_approved).length || 0,
        doctors: response.data.users?.filter(u => u.membership_type === 'doctor').length || 0,
        students: response.data.users?.filter(u => u.membership_type === 'student').length || 0
      }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await axios.put(`${backendUrl}/api/admin/users/${userId}/approve`);
      toast({
        title: 'User Approved',
        description: `${userName} has been approved successfully`
      });
      fetchPendingUsers();
    } catch (error) {
      toast({
        title: 'Approval Failed',
        description: error.response?.data?.detail || 'Failed to approve user',
        variant: 'destructive'
      });
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to reject ${userName}?`)) return;
    
    try {
      await axios.put(`${backendUrl}/api/admin/users/${userId}/reject`);
      toast({
        title: 'User Rejected',
        description: `${userName} has been rejected`
      });
      fetchPendingUsers();
    } catch (error) {
      toast({
        title: 'Rejection Failed',
        description: error.response?.data?.detail || 'Failed to reject user',
        variant: 'destructive'
      });
    }
  };

  const handleRoleChange = async (userId, newRole, userName) => {
    try {
      await axios.put(`${backendUrl}/api/admin/users/${userId}/role?role=${newRole}`);
      toast({
        title: 'Role Updated',
        description: `${userName}'s role updated to ${newRole}`
      });
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Role Update Failed',
        description: error.response?.data?.detail || 'Failed to update role',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to archive ${userName}? This user can be restored later.`)) return;
    
    try {
      await axios.delete(`${backendUrl}/api/admin/users/${userId}`);
      toast({
        title: 'User Archived',
        description: `${userName} has been archived`
      });
      fetchAllUsers();
    } catch (error) {
      toast({
        title: 'Archive Failed',
        description: error.response?.data?.detail || 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  const handleActivate = async (userId, userName) => {
    try {
      await axios.put(`${backendUrl}/api/admin/users/${userId}/activate`);
      toast({ title: 'User Activated', description: `${userName} has been activated` });
      fetchAllUsers();
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to activate', variant: 'destructive' });
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!window.confirm(`Deactivate ${userName}? They will lose access.`)) return;
    try {
      await axios.put(`${backendUrl}/api/admin/users/${userId}/deactivate`);
      toast({ title: 'User Deactivated', description: `${userName} has been deactivated` });
      fetchAllUsers();
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to deactivate', variant: 'destructive' });
    }
  };

  const handleResetPassword = async (userId, userName) => {
    if (!window.confirm(`Reset password for ${userName}? A temporary password will be emailed to them.`)) return;
    try {
      const res = await axios.post(`${backendUrl}/api/admin/users/${userId}/reset-password`);
      toast({ title: 'Password Reset', description: res.data.message || `Password reset email sent to ${userName}` });
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to reset password', variant: 'destructive' });
    }
  };

  const openAddUser = () => {
    setAddUserForm(emptyAddUserForm);
    setAddUserResult(null);
    setAddUserOpen(true);
  };

  const closeAddUser = () => {
    setAddUserOpen(false);
    setAddUserForm(emptyAddUserForm);
    setAddUserResult(null);
  };

  const handleCreateUser = async () => {
    if (!addUserForm.full_name.trim() || !addUserForm.email.trim()) {
      toast({ title: 'Missing fields', description: 'Full name and email are required.', variant: 'destructive' });
      return;
    }
    const payload = { ...addUserForm };
    Object.keys(payload).forEach((key) => {
      if (typeof payload[key] === 'string' && payload[key].trim() === '') {
        payload[key] = null;
      }
    });
    payload.email = String(payload.email || '').trim().toLowerCase();
    payload.full_name = addUserForm.full_name.trim();
    payload.country = payload.country || 'South Africa';

    setAddUserSaving(true);
    try {
      const res = await axios.post(`${backendUrl}/api/admin/users`, payload);
      setAddUserResult(res.data);
      if (activeTab === 'all') {
        fetchAllUsers();
      } else if (activeTab === 'pending') {
        fetchPendingUsers();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(e => e.msg).join('; ')
          : 'Failed to create user';
      toast({ title: 'Create User Failed', description: message, variant: 'destructive' });
    } finally {
      setAddUserSaving(false);
    }
  };

  const copyTempPassword = async () => {
    try {
      await navigator.clipboard.writeText(addUserResult.temporary_password);
      toast({ title: 'Copied', description: 'Temporary password copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Select and copy the password manually.', variant: 'destructive' });
    }
  };

  const fetchAdminNotifications = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/admin/notifications`);
      setAdminNotifications(res.data.notifications || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch notifications', variant: 'destructive' });
    }
  };

  const handleCreateNotification = async () => {
    if (!notifForm.title || !notifForm.message) {
      toast({ title: 'Missing fields', description: 'Title and message are required', variant: 'destructive' });
      return;
    }
    try {
      const res = await axios.post(`${backendUrl}/api/admin/notifications`, notifForm);
      toast({ title: 'Notification Sent', description: res.data.message });
      setNotifForm({ title: '', message: '', user_id: '' });
      fetchAdminNotifications();
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to send notification', variant: 'destructive' });
    }
  };

  const handleDeleteNotification = async (notifId) => {
    if (!window.confirm('Archive this notification? It can be restored later.')) return;
    try {
      await axios.delete(`${backendUrl}/api/admin/notifications/${notifId}`);
      toast({ title: 'Archived', description: 'Notification archived' });
      fetchAdminNotifications();
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to archive notification', variant: 'destructive' });
    }
  };

  const fetchMembershipPrices = async () => {
    setPriceLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/api/admin/membership-prices`);
      setMembershipPrices(res.data.prices || { doctor: 1200, student: 600 });
    } catch (error) {
      console.error('Error fetching prices:', error);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleUpdatePrices = async () => {
    try {
      await axios.put(`${backendUrl}/api/admin/membership-prices`, membershipPrices);
      toast({ title: 'Prices Updated', description: 'Membership prices have been updated' });
    } catch (error) {
      toast({ title: 'Failed', description: error.response?.data?.detail || 'Failed to update prices', variant: 'destructive' });
    }
  };

  const openEditUser = async (u) => {
    setEditUser(u);
    setEditForm({
      full_name: u.full_name || '',
      email: u.email || '',
      preferred_designation: u.preferred_designation || '',
      alternate_contact: u.alternate_contact || '',
      country: u.country || '',
      province: u.province || '',
      city: u.city || '',
      public_email: u.public_email || '',
      public_phone: u.public_phone || '',
      public_address_line1: u.public_address_line1 || '',
      public_address_line2: u.public_address_line2 || '',
      practice_name: u.practice_name || '',
      ahpcsa_number: u.ahpcsa_number || '',
      bhf_number: u.bhf_number || '',
      student_number: u.student_number || '',
      membership_type: u.membership_type || 'doctor',
      role: u.role || 'member',
      is_active: u.is_active !== false,
      is_approved: u.is_approved || false,
      points: u.points || 0,
      payment_status: u.payment_status || 'pending',
      payment_method: u.payment_method || '',
      last_payment_date: u.last_payment_date || '',
      membership_expires: u.membership_expires || '',
    });
    setAuditLogs([]);
    try {
      const res = await axios.get(`${backendUrl}/api/admin/users/${u.id}/audit-log`);
      setAuditLogs(res.data.audit_logs || []);
    } catch (err) {
      // Audit log fetch failure should not block editing
    }
  };

  const handleSaveUser = async () => {
    setEditSaving(true);
    try {
      // Save user details
      const { payment_status, payment_method, last_payment_date, membership_expires, ...detailsForm } = editForm;
      await axios.put(`${backendUrl}/api/admin/users/${editUser.id}/details`, detailsForm);

      // Always send payment update
      const membershipPrices = { doctor: 1200, student: 600 };
      const amount = membershipPrices[editForm.membership_type] || 1200;
      await axios.put(`${backendUrl}/api/admin/users/${editUser.id}/payment`, {
        payment_status,
        payment_method: payment_status === 'paid' ? (payment_method || 'manual') : 'manual',
        amount,
        payment_date: payment_status === 'paid' ? (last_payment_date || new Date().toISOString()) : null,
      });

      toast({ title: 'User Updated', description: `${editForm.full_name}'s details have been updated` });
      setEditUser(null);
      fetchAllUsers();
    } catch (error) {
      toast({ title: 'Update Failed', description: error.response?.data?.detail || 'Failed to update user', variant: 'destructive' });
    } finally {
      setEditSaving(false);
    }
  };

  const searchNotifUsers = async (query) => {
    setNotifUserSearch(query);
    if (!query.trim() || query.length < 2) {
      setNotifUserResults([]);
      return;
    }
    try {
      const res = await axios.get(`${backendUrl}/api/admin/users`, { params: { search: query, limit: 10 } });
      setNotifUserResults(res.data.users || []);
    } catch (e) {
      setNotifUserResults([]);
    }
  };

  const selectNotifUser = (u) => {
    setNotifForm(prev => ({ ...prev, user_id: u.id }));
    setNotifUserSearch(`${u.full_name} (${u.email})`);
    setNotifUserResults([]);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'executive': return 'bg-purple-100 text-purple-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="SYSTEM ADMIN" />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Message */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              System Admin Dashboard
            </h2>
            <p className="text-gray-600">
              Manage users, content, and system settings
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={openAddUser}
              className="bg-green-600 hover:bg-green-700"
              data-testid="add-user-btn"
            >
              <UserPlus size={16} className="mr-1" />
              Add User
            </Button>
            <Button 
              onClick={() => navigate('/admin/council')}
              className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
              data-testid="manage-council-btn"
            >
              Manage Council
            </Button>
            <Button 
              onClick={() => navigate('/admin/news')}
              className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
            >
              Manage News
            </Button>
            <Button 
              onClick={() => navigate('/admin/events')}
              className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
            >
              Manage Events
            </Button>
            <Button 
              onClick={() => navigate('/admin/education')}
              className="bg-[#2c3e7d] hover:bg-[#1e2a5a]"
            >
              Manage Education
            </Button>
            <Button 
              onClick={() => navigate('/admin/event-scanner')}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Event Scanner
            </Button>
            <Button 
              onClick={() => navigate('/admin/brands')}
              className="bg-green-600 hover:bg-green-700"
            >
              Manage Brands
            </Button>
            <Button 
              onClick={() => navigate('/admin/campaigns')}
              className="bg-green-600 hover:bg-green-700"
            >
              Manage Campaigns
            </Button>
            <Button 
              onClick={() => navigate('/admin/creatives')}
              className="bg-green-600 hover:bg-green-700"
            >
              Manage Creatives
            </Button>
            <Button 
              onClick={() => navigate('/admin/analytics')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              View Analytics
            </Button>
          </div>
        </div>
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Users className="text-blue-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                </div>
                <UserCheck className="text-orange-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle className="text-green-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Doctors</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.doctors}</p>
                </div>
                <Crown className="text-yellow-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Students</p>
                  <p className="text-2xl font-bold text-indigo-600">{stats.students}</p>
                </div>
                <Crown className="text-blue-500" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'pending'
                ? 'bg-[#2c3e7d] text-white rounded-t-lg'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            PENDING APPROVALS
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-[#2c3e7d] text-white rounded-t-lg'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            ALL USERS
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'notifications'
                ? 'bg-[#2c3e7d] text-white rounded-t-lg'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Bell size={16} className="inline mr-1" />
            NOTIFICATIONS
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'pricing'
                ? 'bg-[#2c3e7d] text-white rounded-t-lg'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <DollarSign size={16} className="inline mr-1" />
            MEMBERSHIP PRICING
          </button>
        </div>

        {/* Filters (for All Users tab) */}
        {activeTab === 'all' && (
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="executive">Executive</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={membershipFilter} onValueChange={setMembershipFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="doctor">Doctor</SelectItem>
                    <SelectItem value="student">Student</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="mt-4">
                <Button onClick={fetchAllUsers} className="bg-[#2c3e7d]">
                  Apply Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User List - only for pending and all tabs */}
        {(activeTab === 'pending' || activeTab === 'all') && (
        <Card>
          <CardHeader>
            <CardTitle>
              {activeTab === 'pending' ? 'Pending Approvals' : 'All Users'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                <p className="mt-2 text-gray-600">Loading...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeTab === 'pending' && pendingUsers.length === 0 && (
                  <div className="text-center py-8">
                    <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-4 text-gray-600">No pending approvals</p>
                  </div>
                )}
                
                {activeTab === 'all' && users.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-4 text-gray-600">No users found</p>
                  </div>
                )}
                
                {(activeTab === 'pending' ? pendingUsers : users).map(u => (
                  <div
                    key={u.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-4"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{u.full_name}</h4>
                        <span className={`px-2 py-1 text-xs rounded-full ${getRoleBadgeColor(u.role)}`}>
                          {u.role}
                        </span>
                        {u.membership_type === 'doctor' ? (
                          <Crown className="text-yellow-500" size={18} />
                        ) : (
                          <Crown className="text-blue-500" size={18} />
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail size={14} />
                          <span>{u.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          <span>Registered: {new Date(u.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="capitalize">{u.membership_type} | {u.city}, {u.province}</p>
                        {u.practice_name && <p>Practice: {u.practice_name}</p>}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {activeTab === 'pending' ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => handleApprove(u.id, u.full_name)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle size={16} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(u.id, u.full_name)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle size={16} className="mr-1" />
                            Reject
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEditUser(u)}
                            className="text-[#2c3e7d] hover:text-[#1e2a5a] hover:bg-blue-50 whitespace-nowrap"
                          >
                            <Pencil size={16} className="mr-1" />
                            Edit
                          </Button>
                          <Select
                            value={u.role}
                            onValueChange={(newRole) => handleRoleChange(u.id, newRole, u.full_name)}
                          >
                            <SelectTrigger className="w-32">
                              <Shield size={14} className="mr-1" />
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="member">Member</SelectItem>
                              <SelectItem value="executive">Executive</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {u.is_active === false ? (
                            <Button
                              size="sm"
                              onClick={() => handleActivate(u.id, u.full_name)}
                              className="bg-green-600 hover:bg-green-700 whitespace-nowrap"
                            >
                              <Power size={16} className="mr-1" />
                              Activate
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeactivate(u.id, u.full_name)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 whitespace-nowrap"
                            >
                              <Power size={16} className="mr-1" />
                              Deactivate
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResetPassword(u.id, u.full_name)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 whitespace-nowrap"
                          >
                            <KeyRound size={16} className="mr-1" />
                            Reset PW
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(u.id, u.full_name)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Send Notification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Title</label>
                  <Input
                    value={notifForm.title}
                    onChange={(e) => setNotifForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Notification title..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Message</label>
                  <textarea
                    value={notifForm.message}
                    onChange={(e) => setNotifForm(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Notification message..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2c3e7d] min-h-[80px]"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Send to (leave empty for all users)</label>
                  <div className="relative">
                    <Input
                      value={notifUserSearch}
                      onChange={(e) => searchNotifUsers(e.target.value)}
                      placeholder="Search user by name or email..."
                    />
                    {notifUserResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {notifUserResults.map(u => (
                          <div
                            key={u.id}
                            onClick={() => selectNotifUser(u)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-0"
                          >
                            <p className="text-sm font-medium text-gray-900">{u.full_name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {notifForm.user_id && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-xs text-green-600">Selected user ID: {notifForm.user_id}</span>
                        <button
                          onClick={() => { setNotifForm(prev => ({ ...prev, user_id: '' })); setNotifUserSearch(''); }}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <Button onClick={handleCreateNotification} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                  <Send size={16} className="mr-2" />
                  Send Notification
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Sent Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                {adminNotifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                    <p className="text-gray-600">No notifications sent yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {adminNotifications.map(n => (
                      <div key={n.id} className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{n.title}</p>
                          <p className="text-sm text-gray-600 mt-1">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {n.created_at ? new Date(n.created_at).toLocaleString() : ''} | Type: {n.type || 'general'}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteNotification(n.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-2"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Membership Pricing Tab */}
        {activeTab === 'pricing' && (
          <Card>
            <CardHeader>
              <CardTitle>Membership Pricing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {priceLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2c3e7d]"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Doctor Annual Fee (R)</label>
                      <Input
                        type="number"
                        value={membershipPrices.doctor || 0}
                        onChange={(e) => setMembershipPrices(prev => ({ ...prev, doctor: Number(e.target.value) }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Student Annual Fee (R)</label>
                      <Input
                        type="number"
                        value={membershipPrices.student || 0}
                        onChange={(e) => setMembershipPrices(prev => ({ ...prev, student: Number(e.target.value) }))}
                      />
                    </div>
                  </div>
                  <Button onClick={handleUpdatePrices} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                    <Save size={16} className="mr-2" />
                    Update Prices
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={(o) => !o && closeAddUser()}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          {addUserResult ? (
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{addUserResult.user?.full_name}</span> ({addUserResult.user?.email}) was created successfully.
              </p>
              <div className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${addUserResult.email_sent ? 'border-green-300 bg-green-50 text-green-800' : 'border-yellow-300 bg-yellow-50 text-yellow-800'}`}>
                {addUserResult.email_sent
                  ? 'A welcome email with the temporary password was sent to the user.'
                  : 'The welcome email could not be sent — share the temporary password with the user manually.'}
              </div>
              <div>
                <Label>Temporary Password (shown once)</Label>
                <div className="mt-2 flex gap-2">
                  <Input readOnly value={addUserResult.temporary_password || ''} className="font-mono" />
                  <Button type="button" variant="outline" onClick={copyTempPassword}>
                    <Copy size={16} className="mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  The user must change this password on first login.
                </p>
              </div>
              <DialogFooter>
                <Button onClick={closeAddUser} className="bg-[#2c3e7d]">Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                A secure temporary password will be generated and emailed to the user. They must change it on first login.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                <div>
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input value={addUserForm.full_name} onChange={(e) => setAddUserForm(prev => ({ ...prev, full_name: e.target.value }))} />
                </div>
                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input type="email" value={addUserForm.email} onChange={(e) => setAddUserForm(prev => ({ ...prev, email: e.target.value }))} />
                </div>
                <div>
                  <Label>Membership Type <span className="text-red-500">*</span></Label>
                  <Select value={addUserForm.membership_type} onValueChange={(v) => setAddUserForm(prev => ({ ...prev, membership_type: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="doctor">Doctor</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred Designation</Label>
                  <Input value={addUserForm.preferred_designation} onChange={(e) => setAddUserForm(prev => ({ ...prev, preferred_designation: e.target.value }))} placeholder="Dr, Mr, Mrs, Ms" />
                </div>
                <div>
                  <Label>Practice Name</Label>
                  <Input value={addUserForm.practice_name} onChange={(e) => setAddUserForm(prev => ({ ...prev, practice_name: e.target.value }))} />
                </div>
                {addUserForm.membership_type === 'doctor' && (
                  <>
                    <div>
                      <Label>AHPCSA Number</Label>
                      <Input value={addUserForm.ahpcsa_number} onChange={(e) => setAddUserForm(prev => ({ ...prev, ahpcsa_number: e.target.value }))} />
                    </div>
                    <div>
                      <Label>BHF Number</Label>
                      <Input value={addUserForm.bhf_number} onChange={(e) => setAddUserForm(prev => ({ ...prev, bhf_number: e.target.value }))} />
                    </div>
                  </>
                )}
                {addUserForm.membership_type === 'student' && (
                  <div>
                    <Label>Student Number</Label>
                    <Input value={addUserForm.student_number} onChange={(e) => setAddUserForm(prev => ({ ...prev, student_number: e.target.value }))} />
                  </div>
                )}
                <div>
                  <Label>Public Phone</Label>
                  <Input value={addUserForm.public_phone} onChange={(e) => setAddUserForm(prev => ({ ...prev, public_phone: e.target.value }))} />
                </div>
                <div>
                  <Label>Alternate Contact</Label>
                  <Input value={addUserForm.alternate_contact} onChange={(e) => setAddUserForm(prev => ({ ...prev, alternate_contact: e.target.value }))} />
                </div>
                <div>
                  <Label>Province</Label>
                  <Input value={addUserForm.province} onChange={(e) => setAddUserForm(prev => ({ ...prev, province: e.target.value }))} />
                </div>
                <div>
                  <Label>City</Label>
                  <Input value={addUserForm.city} onChange={(e) => setAddUserForm(prev => ({ ...prev, city: e.target.value }))} />
                </div>
                <div>
                  <Label>Public Address Line 1</Label>
                  <Input value={addUserForm.public_address_line1} onChange={(e) => setAddUserForm(prev => ({ ...prev, public_address_line1: e.target.value }))} />
                </div>
                <div>
                  <Label>Public Address Line 2</Label>
                  <Input value={addUserForm.public_address_line2} onChange={(e) => setAddUserForm(prev => ({ ...prev, public_address_line2: e.target.value }))} />
                </div>
                <div className="flex items-center gap-2 md:col-span-2">
                  <input
                    type="checkbox"
                    id="add-user-approved"
                    checked={addUserForm.is_approved}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, is_approved: e.target.checked }))}
                  />
                  <Label htmlFor="add-user-approved" className="text-sm cursor-pointer">
                    Approved (skip membership review)
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAddUser}>Cancel</Button>
                <Button onClick={handleCreateUser} className="bg-[#2c3e7d]" disabled={addUserSaving}>
                  {addUserSaving ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editUser !== null} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Details - {editUser?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div>
              <Label>Full Name</Label>
              <Input value={editForm.full_name || ''} onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editForm.email || ''} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} />
            </div>
            <div>
              <Label>Membership Type</Label>
              <Select value={editForm.membership_type || 'doctor'} onValueChange={(v) => setEditForm(prev => ({ ...prev, membership_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="doctor">Doctor</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role || 'member'} onValueChange={(v) => setEditForm(prev => ({ ...prev, role: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="executive">Executive</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Preferred Designation</Label>
              <Input value={editForm.preferred_designation || ''} onChange={(e) => setEditForm(prev => ({ ...prev, preferred_designation: e.target.value }))} />
            </div>
            <div>
              <Label>Alternate Contact</Label>
              <Input value={editForm.alternate_contact || ''} onChange={(e) => setEditForm(prev => ({ ...prev, alternate_contact: e.target.value }))} />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={editForm.country || ''} onChange={(e) => setEditForm(prev => ({ ...prev, country: e.target.value }))} />
            </div>
            <div>
              <Label>Province</Label>
              <Input value={editForm.province || ''} onChange={(e) => setEditForm(prev => ({ ...prev, province: e.target.value }))} />
            </div>
            <div>
              <Label>City</Label>
              <Input value={editForm.city || ''} onChange={(e) => setEditForm(prev => ({ ...prev, city: e.target.value }))} />
            </div>
            <div>
              <Label>Practice Name</Label>
              <Input value={editForm.practice_name || ''} onChange={(e) => setEditForm(prev => ({ ...prev, practice_name: e.target.value }))} />
            </div>
            <div>
              <Label>AHPCSA Number</Label>
              <Input value={editForm.ahpcsa_number || ''} onChange={(e) => setEditForm(prev => ({ ...prev, ahpcsa_number: e.target.value }))} />
            </div>
            <div>
              <Label>BHF Number</Label>
              <Input value={editForm.bhf_number || ''} onChange={(e) => setEditForm(prev => ({ ...prev, bhf_number: e.target.value }))} />
            </div>
            <div>
              <Label>Student Number</Label>
              <Input value={editForm.student_number || ''} onChange={(e) => setEditForm(prev => ({ ...prev, student_number: e.target.value }))} />
            </div>
            <div>
              <Label>Public Phone</Label>
              <Input value={editForm.public_phone || ''} onChange={(e) => setEditForm(prev => ({ ...prev, public_phone: e.target.value }))} />
            </div>
            <div>
              <Label>Public Email</Label>
              <Input value={editForm.public_email || ''} onChange={(e) => setEditForm(prev => ({ ...prev, public_email: e.target.value }))} />
            </div>
            <div>
              <Label>Public Address Line 1</Label>
              <Input value={editForm.public_address_line1 || ''} onChange={(e) => setEditForm(prev => ({ ...prev, public_address_line1: e.target.value }))} />
            </div>
            <div>
              <Label>Public Address Line 2</Label>
              <Input value={editForm.public_address_line2 || ''} onChange={(e) => setEditForm(prev => ({ ...prev, public_address_line2: e.target.value }))} />
            </div>
            <div>
              <Label>Points</Label>
              <Input type="number" step="0.5" value={editForm.points || 0} onChange={(e) => setEditForm(prev => ({ ...prev, points: parseFloat(e.target.value) || 0 }))} />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm(prev => ({ ...prev, is_active: e.target.checked }))} />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={editForm.is_approved} onChange={(e) => setEditForm(prev => ({ ...prev, is_approved: e.target.checked }))} />
                Approved
              </label>
            </div>

            {/* Payment Section */}
            <div className="col-span-1 md:col-span-2 border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Payment Status</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Payment Status</Label>
                  <Select value={editForm.payment_status || 'pending'} onValueChange={(v) => setEditForm(prev => ({ ...prev, payment_status: v }))}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="unpaid">Unpaid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {editForm.payment_status === 'paid' && (
                  <div>
                    <Label>Payment Method</Label>
                    <Select value={editForm.payment_method || 'manual'} onValueChange={(v) => setEditForm(prev => ({ ...prev, payment_method: v }))}>
                      <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="payfast">PayFast</SelectItem>
                        <SelectItem value="eft">EFT</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {editForm.payment_status === 'paid' && editForm.membership_expires && (
                  <div>
                    <Label>Membership Expires</Label>
                    <Input
                      value={new Date(editForm.membership_expires).toLocaleDateString()}
                      disabled
                      className="mt-2 bg-gray-100"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Audit History */}
            <div className="col-span-1 md:col-span-2 border-t pt-4 mt-2">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Audit History</h4>
              {auditLogs.length === 0 ? (
                <p className="text-sm text-gray-400">No audit history available.</p>
              ) : (
                <div className="max-h-40 overflow-y-auto space-y-2">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="text-xs border-l-2 border-[#2c3e7d] pl-3 py-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">{log.action}</span>
                        <span className="text-gray-400">{new Date(log.timestamp).toLocaleString()}</span>
                      </div>
                      <div className="text-gray-500">by {log.admin_email}</div>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {Object.entries(log.changes).map(([field, vals]) => (
                            <div key={field} className="text-gray-600">
                              <span className="font-medium">{field}</span>:{' '}
                              <span className="text-gray-400">{String(vals.old ?? '—')}</span>
                              {' → '}
                              <span className="text-gray-700">{String(vals.new ?? '—')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleSaveUser} className="bg-[#2c3e7d]" disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;